/**
 * Role/session maintenance deferred from RoleContext critical path (Phase 1).
 */

import { kvGet, kvSet } from '../infrastructure/storage/kvAccess';
import sessionRecorder from '../utils/sessionRecorder';
import {
  loadWorkersForBusiness,
  persistWorkersForBusiness,
  migrateWorkersExpensesPermission
} from '../utils/workersStorage';
import {
  pruneStaleWorkerSessions,
  reconcileAbandonedWorkerSessions
} from '../utils/workerSessionSanitizer';
import {
  resolveLocalManagerCode,
  shouldSyncManagerCodeWithSupabase
} from '../utils/localManagerCode';
import { markBootGate, measureBoot } from '../infrastructure/telemetry/bootTelemetry';

/**
 * @param {object} params
 * @param {string|null} params.businessId
 * @param {{ type?: string, id?: string }|null} params.activeRole
 * @param {object|null} params.business
 */
export async function runStartupRoleMaintenance({
  businessId,
  activeRole,
  business
}) {
  markBootGate('role_maintenance_start');

  let biz = business;

  if (businessId) {
    const pruneResult = pruneStaleWorkerSessions(businessId);
    if (pruneResult.pruned > 0) {
      console.info('[RoleContext] Stale worker sessions pruned on load:', pruneResult.details);
    }
    if (!activeRole || activeRole.type === 'manager') {
      const reconcileResult = sessionRecorder.reconcileAbandonedWorkerSessions(businessId);
      if (reconcileResult.closed > 0) {
        console.info(
          '[RoleContext] Abandoned worker sessions closed on load:',
          reconcileResult.details
        );
      }
    } else if (activeRole.type === 'worker') {
      sessionRecorder.reconcileAbandonedWorkerSessions(businessId, activeRole.id);
    }
  }

  const { workers: loadedWorkers, migrated: workersMigrated } = loadWorkersForBusiness(businessId);
  const migratedWorkers = migrateWorkersExpensesPermission(loadedWorkers);
  const needsExpenseMigration = migratedWorkers.some(
    (w, i) => w.permissions?.expenses !== loadedWorkers[i]?.permissions?.expenses
  );
  if (workersMigrated || needsExpenseMigration) {
    await persistWorkersForBusiness(businessId, migratedWorkers, {
      allowEmpty: migratedWorkers.length === 0
    });
  }

  const localManagerCode = resolveLocalManagerCode(businessId);
  if (localManagerCode) {
    kvSet('managerCode', localManagerCode);
    if (biz && !biz.manager_code) {
      biz = { ...biz, manager_code: localManagerCode };
      kvSet('currentBusiness', biz);
    }
  } else if (biz?.manager_code) {
    kvSet('managerCode', biz.manager_code);
  } else if (biz && shouldSyncManagerCodeWithSupabase(biz)) {
    try {
      const { supabase } = await import('../config/supabase');
      const { data, error } = await supabase
        .from('businesses')
        .select('manager_code')
        .eq('id', biz.id)
        .single();
      if (!error && data?.manager_code) {
        kvSet('managerCode', data.manager_code);
        const updatedBusiness = { ...biz, manager_code: data.manager_code };
        kvSet('currentBusiness', updatedBusiness);
        const currentUser = kvGet('currentUser');
        if (currentUser && typeof currentUser === 'object') {
          kvSet('currentUser', { ...currentUser, business: updatedBusiness });
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[RoleContext] Manager code load skipped:', error?.message || error);
      }
    }
  }

  markBootGate('role_maintenance_done');
  measureBoot('role_maintenance_start', 'role_maintenance_done', 'role_maintenance');

  const managerCode = kvGet('managerCode') || biz?.manager_code || localManagerCode || null;

  return { workers: migratedWorkers, business: biz, managerCode };
}
